import { BudgetAmountStatus } from '@/components/cashflow/budget/BudgetAmountStatus';
import { BudgetBarChart } from '@/components/cashflow/budget/BudgetBarChart';
import { useBudgetContext } from '@/components/cashflow/budget/BudgetContext';
import { BudgetControls } from '@/components/cashflow/budget/BudgetControls';
import { BudgetTransactionEntriesTable } from '@/components/cashflow/budget/BudgetTransactionEntriesTable';
import { TRANSACTION_ENTRIES_TABLE_Z_INDEX } from '@/components/cashflow/transactions/TransactionEntriesTable';
import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { useDrawer } from '@/components/common/DrawerManager';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { BigHeader } from '@/components/common/Typography';
import { Box, Flex } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { getBudgetPurposeLabel } from '@common/react/budget';
import { ParentSize } from '@visx/responsive';
import { useRef } from 'react';

export const BudgetCategoryGroupDetailDrawer = ({
    id: groupId,
}: {
    id: string;
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { showDrawer } = useDrawer();
    const { transactionEntries, categoryGroupsWithSpending, loading } =
        useBudgetContext();

    const group = categoryGroupsWithSpending.find((g) => g.id === groupId);
    assert(group, 'Category group not found');

    const transactionEntriesForCategoryGroup = transactionEntries.filter(
        (t) => t.category.categoryGroup.id === group.id
    );

    const budgetedAmount = group.budgetedAmount;
    const actualAmount = group.spendingAmount;
    const amountLeft = budgetedAmount - actualAmount;
    const percentage = Math.abs(
        (actualAmount - budgetedAmount) / budgetedAmount
    );

    return (
        <Flex direction="column" ref={scrollRef} h="100%" overflowY="scroll">
            <FlexRow
                py={8}
                px={10}
                bg="gray.50"
                justify="space-between"
                align="center"
                position="sticky"
                top={0}
                zIndex={TRANSACTION_ENTRIES_TABLE_Z_INDEX + 1}
            >
                <FlexCol gap={2}>
                    <BigHeader>{group.name}</BigHeader>
                    <ArrowLinkText
                        arrowDirection="left"
                        onClick={() =>
                            showDrawer({
                                drawerKey: 'budget-purpose-detail',
                                entityId: group.budgetPurpose,
                            })
                        }
                    >
                        Back to {getBudgetPurposeLabel(group.budgetPurpose)}
                    </ArrowLinkText>
                </FlexCol>
                <BudgetControls
                    budgetPurpose={group.budgetPurpose}
                    groupId={group.id}
                />
            </FlexRow>

            <FlexCol pt={10} px={10}>
                <BudgetAmountStatus
                    budgetedAmount={budgetedAmount}
                    budgetPurpose={group.budgetPurpose}
                    amountLeft={amountLeft}
                    percentage={percentage}
                    withPrefix={true}
                    loading={loading}
                />

                <Box mt={8}>
                    <ParentSize>
                        {({ width }) => (
                            <BudgetBarChart
                                width={width}
                                budgetedAmount={budgetedAmount}
                                actualAmount={actualAmount}
                                budgetPurpose={group.budgetPurpose}
                            />
                        )}
                    </ParentSize>
                </Box>

                <BudgetTransactionEntriesTable
                    title={group.name}
                    transactionEntries={transactionEntriesForCategoryGroup}
                    loading={loading}
                    scrollRef={scrollRef}
                />
            </FlexCol>
        </Flex>
    );
};
